import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import CloseIcon from '@mui/icons-material/Close';
import LicensePanel from '../modal/LicensePanel';

type BadgeKind = 'ok' | 'soon' | 'expired' | 'none';

const expBadge = (date: string | null): { kind: BadgeKind; label: string } => {
  if (!date) return { kind: 'none', label: '—' };
  const exp = new Date(date);
  const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
  if (days < 0) return { kind: 'expired', label: t('Expired') || 'Expired' };
  if (days <= 60) return { kind: 'soon', label: `${days} ${t('DaysShort') || 'd'}` };
  return { kind: 'ok', label: exp.toLocaleDateString('en-GB') };
};

const bStyle = (kind: BadgeKind): React.CSSProperties => {
  const map: Record<BadgeKind, [string, string]> = {
    ok:      ['var(--color-success-bg)',  'var(--color-success)'],
    soon:    ['var(--color-warning-bg)',  'var(--color-warning)'],
    expired: ['var(--color-danger-bg)',   'var(--color-danger)'],
    none:    ['var(--btn-user-in)',        'var(--text-color-calendar-week)'],
  };
  const [bg, color] = map[kind];
  return { background: bg, color, fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' };
};

interface Props {
  onClose: () => void;
  baseUrl: string;
  canEdit?: boolean;
}

type View = 'list' | 'edit';

const DriverLicensesPanel: React.FC<Props> = ({ onClose, baseUrl, canEdit }) => {
  const [view, setView] = useState<View>('list');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [licRes, usersRes] = await Promise.allSettled([
        axios.get('hr-driver-license/company'),
        axios.get('users'),
      ]);
      if (licRes.status === 'fulfilled') setLicenses(Array.isArray(licRes.value.data) ? licRes.value.data : []);
      if (usersRes.status === 'fulfilled') setAllUsers(Array.isArray(usersRes.value.data) ? usersRes.value.data : []);
    } catch {
      /* interceptor */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = licenses.filter((l) => {
    if (!search) return true;
    const name = `${l.user?.first_name ?? ''} ${l.user?.last_name ?? ''}`.toLowerCase();
    return name.includes(search.toLowerCase()) || (l.license_number ?? '').toLowerCase().includes(search.toLowerCase());
  });

  const valid   = licenses.filter((l) => l.expiry_date && new Date(l.expiry_date) >= new Date()).length;
  const expired = licenses.filter((l) => l.expiry_date && new Date(l.expiry_date) < new Date()).length;

  const licenseUserIds = new Set(licenses.map((l) => l.userId ?? l.user?.id));
  const usersWithoutLicense = allUsers.filter((u) => !licenseUserIds.has(u.id));

  const userSelectOptions = useMemo(() =>
    usersWithoutLicense.map((u) => ({
      value: u.id,
      label: `${u.first_name} ${u.last_name}${u.position ? ` — ${u.position}` : ''}`,
    })),
    [usersWithoutLicense],
  );

  const openEdit = (userId: number | null) => { setSelectedUserId(userId); setView('edit'); };
  const afterSave = () => { load(); setView('list'); setSelectedUserId(null); };

  // Edit/add view — renders LicensePanel for the selected user
  if (view === 'edit') {
    return (
      <LicensePanel
        userId={selectedUserId}
        usersForPicker={selectedUserId == null ? userSelectOptions : undefined}
        baseUrl={baseUrl}
        onClose={() => { setView('list'); setSelectedUserId(null); }}
        onSaved={afterSave}
      />
    );
  }

  // List view
  return (
    <div className="w-full h-full flex flex-col bg-[var(--bg-form)] text-[var(--text-color)]" style={{ minHeight: 0 }}>
      <div className="flex justify-between items-center py-[10px] px-[13px] bg-[var(--bg-top)]" style={{ borderBottom: 'var(--border-input)' }}>
        <span className="text-[14px] font-bold flex items-center gap-[8px]">🪪 {t('DriverLicenses') || 'Driver Licenses'}</span>
        <div className="flex gap-[6px] items-center">
          {canEdit && (
            <button
              type="button"
              className="btn-primary"
              style={{ width: 30, height: 30, padding: 0, justifyContent: 'center' }}
              title={t('Add') as string}
              onClick={() => openEdit(null)}
            >＋</button>
          )}
          <button type="button" className="btn-icon" onClick={onClose}><CloseIcon sx={{ fontSize: 16 }} /></button>
        </div>
      </div>

      {!loading && licenses.length > 0 && (
        <div className="flex gap-[8px] px-[12px] py-[8px] flex-wrap" style={{ borderBottom: 'var(--border-input)' }}>
          <span style={bStyle('ok')}>{valid} {t('Valid') || 'valid'}</span>
          {expired > 0 && <span style={bStyle('expired')}>{expired} {t('Expired') || 'expired'}</span>}
        </div>
      )}

      <div className="flex-1 overflow-auto p-[10px]" style={{ minHeight: 0 }}>
        {loading ? (
          <div className="text-center text-[12px] text-[var(--text-color-calendar-week)] py-[20px]">…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[12px] text-[var(--text-color-calendar-week)] py-[24px]">
            {t('NoLicences') || 'No licenses found'}
          </div>
        ) : (
          filtered.map((lic) => {
            const exp = expBadge(lic.expiry_date ?? null);
            const name = `${lic.user?.first_name ?? ''} ${lic.user?.last_name ?? ''}`.trim() || `User #${lic.userId}`;
            const userId = lic.userId ?? lic.user?.id;
            return (
              <div
                key={lic.id}
                onClick={() => canEdit && userId && openEdit(userId)}
                className={`mb-[8px]${canEdit ? ' cursor-pointer' : ''}`}
                style={{ background: 'var(--btn-user-form)', border: 'var(--border-input)', borderRadius: 8, padding: '10px 11px' }}
              >
                <div className="flex justify-between items-start gap-[8px]">
                  <div>
                    <div className="text-[13px] font-bold">{name}</div>
                    <div className="text-[11px] text-[var(--text-color-calendar-week)] mt-[1px]">
                      {[
                        lic.license_number || (t('NoLicence') || 'No license number'),
                        lic.vehicle_class ? `Class ${lic.vehicle_class}` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {lic.expiry_date && (
                    <span style={bStyle(exp.kind)}>{exp.label}</span>
                  )}
                </div>
                {(lic.issue_date || lic.expiry_date) && (
                  <div className="flex gap-[6px] mt-[7px] flex-wrap items-center">
                    {lic.issue_date && (
                      <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
                        {t('IssueDate') || 'Issued'}: {new Date(lic.issue_date).toLocaleDateString('en-GB')}
                      </span>
                    )}
                    {lic.expiry_date && (
                      <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
                        {t('ExpiryDate') || 'Expires'}: {new Date(lic.expiry_date).toLocaleDateString('en-GB')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DriverLicensesPanel;
